import { useEffect, useState, Dispatch, SetStateAction } from "react";
import { useDataContext } from "../../DataProvider/DataContext";
import { Accordion, AccordionDetails, AccordionSummary, ButtonBase, CircularProgress, IconButton, Tooltip } from '@mui/material';
import palette from "../../../utilities/palette";
import ClusterSelectBox from "./ClusterSelectBox";
import { getJsonContent, handleFNV23Versioning, populateMachineListErrorsSection, populateSupplierInVariantList } from "../../../utilities/utilityFunctions";
import { saveDataToLocalStorage } from "../../../utilities/localStorageUtils";
import { createSelectionCriteriaList, processClusterSingleItemData, reorderClusterListBySwInstallationSequence } from "../../../utilities/ecuCatalogeUtils";
import { srpTemplate, type SRPDMachineListInterface, type SRPDModuleInformationInterface, type SRPDPreAssemblyImageInterface, type SRPDSelectionCriteriaListInterface, type StateSRPdParentLevelInterface } from "../../../models/SRPSchema";
import { ecucCataSelectObjTemplate } from "../../../models/ECUCInterfaces";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FlexColumnDiv, FlexRowDiv } from "../../styledComponents/Components.styles";
import LabelField from "../formFields/LabelField";
import { importProcessDataTemplate } from "../../../models/ImportProcessData";
import { SelectOption } from "../../../utilities/dropDownOptions";


const ECUCataClusterSelect = ({
    setProjectKey,
    selectedKeyPersonOption, setSelectedKeyPersonOption,
    selectedResponsibleOptions, setSelectedResponsibleOptions,
    textFieldValues, setTextFieldValues,
    moduleInformation,
    machineList, setMachineList,
    preassemblyImage, setPreassemblyImage,
    setMachineListErrors,
}: {
    selectedKeyPersonOption: SelectOption,
    setSelectedKeyPersonOption: Dispatch<SetStateAction<SelectOption>>,
    selectedResponsibleOptions: SelectOption[], 
    setSelectedResponsibleOptions: Dispatch<SetStateAction<SelectOption[]>>,
    textFieldValues: StateSRPdParentLevelInterface, 
    setTextFieldValues: Dispatch<SetStateAction<StateSRPdParentLevelInterface>>,
    moduleInformation: SRPDModuleInformationInterface, 
    machineList: SRPDMachineListInterface[], 
    setMachineList: Dispatch<SetStateAction<SRPDMachineListInterface[]>>,
    preassemblyImage: SRPDPreAssemblyImageInterface,
    setPreassemblyImage: Dispatch<SetStateAction<SRPDPreAssemblyImageInterface>>,
    setMachineListErrors: Dispatch<SetStateAction<any[]>>,
    setProjectKey: Dispatch<SetStateAction<string>>,
}) => {
    
    const { 
        ecuCataSelectObj, setECUCataSelectObj, 
        clusterSelectObj,
        setECUData,
        setFormProcessingState,
        apiService,
        architectureType,
        api,
        importProcessData,
        setImportProcessData,
        tempData,
        setTempData
    } = useDataContext();
    const [isLoading, setIsLoading] = useState(false);
    

    const [errorMessageProcessCluster, setErrorMessageProcessCluster] = useState("");
    const [expanded, setExpanded] = useState("panel0-0");



    // Helper function to fetch and process cluster data
    async function fetchAndProcessClusterData(
        cluster: any,
        selectionCriteriaList: SRPDSelectionCriteriaListInterface[]
    ) {
        if (!apiService) {
            throw new Error("ECUC API is not initialized");
        }

        let clusterDataResponse = await apiService.getClusterData(
            ecuCataSelectObj.programs.value.map((program) => program.value),
            ecuCataSelectObj.module.value.value,
            ecuCataSelectObj.machines.value.map((machine) => machine.value),
            ecuCataSelectObj.variants.value.map((variant) => variant.value),
            [`${cluster.value}`]
        );

        if (!clusterDataResponse.isSuccess) {
            clusterDataResponse = await apiService.getClusterDataAlternative(
                `${cluster.value}`,
            );

            if (!clusterDataResponse.isSuccess) {
                throw new Error(clusterDataResponse.message || "Failed to fetch cluster data");
            }
        }

        const clusterData = Array.isArray(clusterDataResponse.data) && clusterDataResponse.data.length > 0
            ? clusterDataResponse.data[0]
            : clusterDataResponse.data;
        return processClusterSingleItemData(clusterData, selectionCriteriaList);
    }

    // Helper function to update cluster list with new data
    function updateClusterList(
        currentClusterList: any[],
        mapClusterNameData: { [key: string]: any },
        remainingCluster: Set<string>,
        updateType: "BACKUP" | "PRIMARY" = "PRIMARY"
    ) {
        console.log("Updating cluster list:", "currentClusterList", currentClusterList, "mapClusterNameData", mapClusterNameData, "remainingCluster", remainingCluster);
        const pendingRemoveIndices: number[] = [];

        for (let i = 0; i < currentClusterList.length; i++) {
            const clusterName = currentClusterList[i].clusterName;
            
            if (remainingCluster.has(clusterName)) {
                remainingCluster.delete(clusterName);
            }

            if (mapClusterNameData[clusterName]) {
                if (updateType === "PRIMARY") {
                    currentClusterList[i] = {
                        ...structuredClone(srpTemplate.machineList[0].variantList[0].clusterList[0]),
                        ...currentClusterList[i],
                    }
                }

                if (updateType === "BACKUP") {
                    currentClusterList[i] = {
                        ...structuredClone(srpTemplate.machineList[0].variantList[0].clusterList[0]),
                        ...mapClusterNameData[clusterName],
                    }
                }

            } else {
                pendingRemoveIndices.push(i);
            }
        }

        removeElementsByIndices(currentClusterList, pendingRemoveIndices);

        for (let clusterName of remainingCluster) {
            currentClusterList.push({
                ...structuredClone(srpTemplate.machineList[0].variantList[0].clusterList[0]),
                ...mapClusterNameData[clusterName]
            });
        }
    }

    // Helper function to handle post-processing tasks
    async function handlePostProcessing(
        newMachineList: SRPDMachineListInterface[],
        pjKey: string,
        newTextFieldValues: StateSRPdParentLevelInterface
    ) {
        let newSelectedKeyPersonOption = selectedKeyPersonOption;
        let newSelectedResponsibleOptions = selectedResponsibleOptions;
        if (importProcessData) {
            newTextFieldValues.srpdName = importProcessData?.data.srpdName;
            newTextFieldValues.srpdVersion = importProcessData?.data.srpdVersion;
            newTextFieldValues.srpAssignee = importProcessData?.data.srpAssignee;
            newTextFieldValues.electricalArchitecture = importProcessData?.data.electricalArchitecture;
            newTextFieldValues.srpSchemaVersion = importProcessData?.data.srpSchemaVersion;
            newTextFieldValues.srpWatchers = importProcessData?.data.srpWatchers || [];

            newSelectedKeyPersonOption = importProcessData?.data.srpAssignee ? {
                label: importProcessData.data.srpAssignee,
                value: importProcessData.data.srpAssignee,
            } : selectedKeyPersonOption;

            newSelectedResponsibleOptions = importProcessData?.data.srpWatchers ? importProcessData.data.srpWatchers.map((watcher: string) => ({
                label: watcher,
                value: watcher,
            })) : selectedResponsibleOptions;
        }
        setSelectedKeyPersonOption(newSelectedKeyPersonOption);
        setSelectedResponsibleOptions(newSelectedResponsibleOptions);

        reorderClusterListBySwInstallationSequence({ machineList: newMachineList });
        setMachineList(newMachineList);

        populateSupplierInVariantList(newMachineList);
        populateMachineListErrorsSection(newMachineList, setMachineListErrors);

        setPreassemblyImage((prevState) => {
            const newPreassemblyImage = structuredClone(prevState);
            newPreassemblyImage.clusterList = [];
            return newPreassemblyImage;
        });


        setTextFieldValues(newTextFieldValues);


        const prefilledJSONContent = getJsonContent({
            textFieldValues: tempData.current.textFieldValues,
            selectedKeyPersonOption: newSelectedKeyPersonOption,
            selectedResponsibleOptions: newSelectedResponsibleOptions,
            moduleInformation: tempData.current.moduleInformation,
            machineList: tempData.current.machineList,
            preassemblyImage,
        });

        console.log(`prefilledJSONContent:`, prefilledJSONContent);

        setECUData((data) => ({
            ...data,
            prefilledJSONContent: prefilledJSONContent,
        }));

        setFormProcessingState("FORM_EDIT");
    }



    async function selectClusters() {
        try {
            setErrorMessageProcessCluster("");
            setIsLoading(true);

            // Create selection criteria from vehicle programs
            const selectionCriteriaList = createSelectionCriteriaList(moduleInformation.vehiclePrograms);
            const newMachineList = structuredClone(machineList);
            let pjKey = "";

            // Validate required API parameters
            const programIds = ecuCataSelectObj.programs.value.map((program) => program.value);
            const machineIds = ecuCataSelectObj.machines.value.map((machine) => machine.value);
            const variantIds = ecuCataSelectObj.variants.value.map((variant) => variant.value);
            const moduleId = ecuCataSelectObj.module.value?.value;

            if (programIds.length === 0 || machineIds.length === 0 || variantIds.length === 0 || !moduleId) {
                throw new Error("Missing required IDs for API call");
            }

            // Process each machine and variant
            for (let i = 0; i < clusterSelectObj.length; i++) {
                const machine = clusterSelectObj[i];

                for (let j = 0; j < machine.variantList.length; j++) {
                    const variant = machine.variantList[j];
                    const currentClusterList = newMachineList[i].variantList[j].clusterList;
                    const currentBackupVariant = tempData.current.machineList[i].variantList[j];
                    const currentBackupClusterList = currentBackupVariant.clusterList;
                    
                    const addedClusters = variant.addedClusters;

                    const mapClusterNameData: { [key: string]: any } = {};
                    const remainingCluster = new Set<string>();

                    // Fetch and process cluster data for each added cluster
                    for (let cluster of variant.addedClusters) {
                        const processedData = await fetchAndProcessClusterData(
                            cluster,
                            selectionCriteriaList
                        );

                        if (!pjKey) {
                            pjKey = processedData.clusterName?.split("_")[0];
                            setProjectKey(pjKey);
                        }

                        mapClusterNameData[processedData.clusterName] = processedData;
                        remainingCluster.add(processedData.clusterName);
                    }

                    const backupRemainingCluster = new Set<string>(remainingCluster);

                    // Update the cluster list with new data
                    updateClusterList(currentClusterList, mapClusterNameData, remainingCluster);

                    // Update backup cluster data from temporary state

                    updateClusterList(currentBackupClusterList, mapClusterNameData, backupRemainingCluster, "BACKUP");
                    // Handle FNV23 versioning if needed
                    if (architectureType !== "FNV4") {
                        await handleFNV23Versioning(currentClusterList, addedClusters, api);
                    }
                    newMachineList[i].variantList[j].clusterList = currentClusterList;
                    tempData.current.machineList[i].variantList[j].clusterList = currentBackupClusterList;
                    setTempData(tempData.current);                    
                }
            }

            // Create updated text field values
            const moduleName = moduleInformation.moduleName;
            const srpName = `${pjKey}_${moduleName}`.replace(/ /g, "_");
            const newTextFieldValues = {
                ...textFieldValues,
                srpdName: srpName
            };
            setTempData({
                ...tempData.current,
                textFieldValues: newTextFieldValues,
            });

            // Handle all post-processing tasks
            console.log("Machine list before post-processing:", newMachineList);
            await handlePostProcessing(newMachineList, pjKey, newTextFieldValues);
            setIsLoading(false);

        } catch (error: any) {
            setErrorMessageProcessCluster("Error processing clusters data: " + error.message);
            console.error("Error selecting clusters:", error);
            setIsLoading(false);
        }
    }

    // Helper function to remove elements by indices
    function removeElementsByIndices(originalArray: any[], indices: any[]) {
        indices.sort((a, b) => b - a);
    
        for (const index of indices) {
            originalArray.splice(index, 1);
        }
    
    }

    const [isAtLeastOneVariantSelected, setIsAtLeastOneVariantSelected] = useState(false);

    useEffect(() => {
        const isAtLeastOneVariantSelected = clusterSelectObj.some(machine => 
            machine.variantList.some(variant => variant.addedClusters.length > 0)
        );
        setIsAtLeastOneVariantSelected(isAtLeastOneVariantSelected);

        saveDataToLocalStorage('clusterSelectObj', clusterSelectObj);

    }, [clusterSelectObj]);
    

    return(
        <div
            style={{
                    display: 'flex', 
                    flexDirection: 'column', 
                    width: '100%',
                    height: "85vh",
                }}
        >
                <h2 style={{fontFamily: 'FordF-1-Semibold', fontSize: '30px', marginLeft: '2vw', color: palette.main}}>Cluster Selection</h2>
            

                <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'auto',
                        flex: 1,
                    }}>

                    {clusterSelectObj.map((machineEntry, machineIndex) => 
                        machineEntry.variantList.map((variantEntry, variantIndex) => (
                            <FlexColumnDiv
                                style={{width: '70%', alignSelf: 'center'}}
                                key={`clusterSelectAccordionDetails-machine-${machineIndex}-variant-${variantIndex}`}
                            >
                                {/* add seperator if not first variant */}                                
                                
                                <Accordion slotProps={{ transition: { unmountOnExit: true } }} expanded={expanded === `panel${machineIndex}-${variantIndex}`} onChange={(event, isExpanded) => setExpanded(isExpanded ? `panel${machineIndex}-${variantIndex}` : ``)}     
                                    id={`panel${machineIndex}-${variantIndex}`}
                                    sx={{ 
                                        background: 'transparent',
                                        padding: '20px 0',
                                        boxShadow: 'none',

                                    }}
                                >

                                    <ButtonBase sx={{width: '100%'}}>
                                    <AccordionSummary
                                        aria-controls={`panel${machineIndex}-${variantIndex}-content`}
                                        id={`panel${machineIndex}-${variantIndex}-header`}
                                        sx={{
                                            ":hover": {
                                                backgroundColor: palette.header.main
                                            },
                                            borderRadius: '10px'
                                        }}
                                    > 
                                    {expanded === `panel${machineIndex}-${variantIndex}` && <ExpandMoreIcon sx={{fontSize: '30px'}}/>}

                                        <FlexRowDiv style={{
                                            gap: '100px',
                                            borderLeft: `4px solid ${palette.main}`,
                                            borderRadius: '4px',
                                            // minHeight: '',
                                            padding: '10px 20px 10px 20px',
                                            
                                            }}>
                                            <LabelField
                                                label={"Variant Name"}
                                                value={variantEntry.label}
                                                cursor="pointer"
                                            />
                                            <LabelField
                                                label={"Variant Description"}
                                                value={variantEntry.description ?? "N/A"}
                                                cursor="pointer"
                                            />
                                        </FlexRowDiv>
                                    </AccordionSummary>
                                    </ButtonBase>


                                    <AccordionDetails>
                                        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                                            <ClusterSelectBox machineIndex={machineIndex} variantEntry={variantEntry} variantIndex={variantIndex}/>
                                        </div>                                    
                                    </AccordionDetails>

                                </Accordion>
                            </FlexColumnDiv>)))}
                </div>

                <div style={{
                    marginTop: "30px", 
                    marginBottom: "30px", 
                    alignSelf: 'center',
                    display: 'flex', flexDirection: 'column', gap: '10px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                }}>
                    {isLoading ? <CircularProgress size={30} sx={{
                        color: palette.primary.main,
                    }}/>
                        :
                        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'transparent'}}>
                            {errorMessageProcessCluster && (
                                <div style={{ color: palette.stepper.error, marginBottom: '10px' }}>
                                    An error occurred while processing cluster data. If this problem persists, please contact cmeyer75 or vluong5 for support.
                                </div>
                            )}
                            <div style={{display: 'flex', flexDirection: 'row', gap: '20px', background: 'transparent'}}>
                            <Tooltip title="Go Back">   
                                <IconButton
                                    sx={{
                                        borderRadius: '20px',
                                        backgroundColor: palette.main,
                                        color: palette.primary.text, 
                                        ":hover": {
                                            backgroundColor: palette.hover,
                                        },
                                        ":disabled": {
                                            backgroundColor: palette.disabled.background,
                                            color: palette.disabled.text,
                                        }
                                    }}
                                    onClick={()=>{
                                        setECUCataSelectObj((prev) => ({
                                            ...structuredClone(ecucCataSelectObjTemplate),
                                            programs: {
                                                ...prev.programs,
                                                value: []
                                            }
                                        }));
                                        setImportProcessData(structuredClone(importProcessDataTemplate));
                                        setFormProcessingState("ECUC_SELECT");
                                    }}
                                >
                                    <ArrowBackIcon style={{
                                        color: palette.primary.text,
                                    }}/>
                                </IconButton>
                            </Tooltip>
                                <Tooltip title="Go Next">
                                    <IconButton
                                        sx={{
                                            borderRadius: '20px',
                                            backgroundColor: !isAtLeastOneVariantSelected ? palette.disabled.background : palette.main,
                                            color: !isAtLeastOneVariantSelected ? palette.disabled.text : palette.primary.text, 
                                            ":hover": {
                                                backgroundColor: !isAtLeastOneVariantSelected ? palette.disabled.background : palette.hover,
                                            },
                                            ":disabled": {
                                                backgroundColor: palette.disabled.background,
                                                color: palette.disabled.text,
                                            }
                                        }}
                                        disabled={!isAtLeastOneVariantSelected}
                                        onClick={() => selectClusters()}
                                    >
                                        <ArrowForwardIcon style={{
                                            color: !isAtLeastOneVariantSelected ? palette.disabled.text : palette.primary.text,
                                        }}/>
                                    </IconButton>
                                </Tooltip>
                            </div>
                        </div>
                    }

                </div>
        </div>
);
}

export default ECUCataClusterSelect;


